import React from 'react';
import '../css/PrivacyPage.css';
import { Shield, Mail, Lock, FileText, Globe, AlertCircle } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-policy-section">
      <div className="privacy-content">
        <div className="privacy-header">
          <Shield size={48} className="privacy-icon" />
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-intro">
            This Privacy Policy applies to all personal information collected by Nfinity Holdings Pty Ltd 
            ("we", "us", or "our") via the website located at nimbli.io ("Website").
          </p>
        </div>

        <div className="privacy-sections">
          {/* What information do we collect */}
          <section className="privacy-section">
            <div className="section-icon">
              <FileText size={32} />
            </div>
            <h2 className="section-title">What information do we collect?</h2>
            <p className="section-text">
              The kind of personal information that we collect from you depends on how you use the Website.
            </p>
            <p className="section-subtitle">It may include:</p>
            <ul className="privacy-list">
              <li>Name and company name</li>
              <li>Address</li>
              <li>Telephone number</li>
              <li>Email address</li>
            </ul>
          </section>

          {/* Types of information */}
          <section className="privacy-section">
            <div className="section-icon">
              <AlertCircle size={32} />
            </div>
            <h2 className="section-title">Types of information</h2>
            <p className="section-text">
              The Privacy Act 1988 (Cth) defines types of information, including Personal Information and Sensitive Information.
            </p>
            
            <div className="info-card">
              <h3 className="info-card-title">Personal Information</h3>
              <p className="info-card-text">
                Personal Information means information or an opinion about an identified individual or an individual 
                who is reasonably identifiable:
              </p>
              <ul className="privacy-list">
                <li>whether the information or opinion is true or not; and</li>
                <li>whether the information or opinion is recorded in a material form or not.</li>
              </ul>
              <p className="info-card-note">
                If the information does not disclose your identity or enable your identity to be ascertained, 
                it will generally not be classified as "Personal Information".
              </p>
            </div>

            <div className="info-card">
              <h3 className="info-card-title">Sensitive Information</h3>
              <p className="info-card-text">
                Sensitive Information includes information or opinion about an individual's:
              </p>
              <ul className="privacy-list">
                <li>racial or ethnic origin</li>
                <li>political opinions</li>
                <li>membership of a political association</li>
                <li>religious or philosophical beliefs</li>
                <li>membership of a trade union or other professional body</li>
                <li>criminal record</li>
                <li>health information</li>
              </ul>
              <p className="info-card-note">
                Sensitive Information will only be used by us:
              </p>
              <ul className="privacy-list">
                <li>for the primary purpose for which it was obtained</li>
                <li>for a secondary purpose directly related to the primary purpose</li>
                <li>with your consent, or where required or authorised by law</li>
              </ul>
            </div>
          </section>

          {/* How we collect */}
          <section className="privacy-section">
            <div className="section-icon">
              <FileText size={32} />
            </div>
            <h2 className="section-title">How we collect your Personal Information</h2>
            <p className="section-text">
              We may collect Personal Information from you whenever you provide it through the Website, 
              a related app, or by any other means.
            </p>
            <p className="section-text">
              We may also collect cookies from your computer, which enable us to recognise when you use 
              the Website and to help customise your experience. As a general rule, however, it is not 
              possible to identify you personally from our use of cookies.
            </p>
            <p className="section-text">
              We generally do not collect Sensitive Information. When we do, we comply with the principles 
              outlined above.
            </p>
            <p className="section-text">
              Where reasonable and practicable, we collect your Personal Information directly from you. 
              Sometimes, we may receive information from a third party - in such cases, we will take steps 
              to make you aware of this.
            </p>
          </section>

          {/* Purpose of collection */}
          <section className="privacy-section">
            <div className="section-icon">
              <Lock size={32} />
            </div>
            <h2 className="section-title">Purpose of collection</h2>
            <p className="section-text">We collect Personal Information to:</p>
            <ul className="privacy-list">
              <li>provide you with the best possible service experience on the Website</li>
              <li>keep in touch with you about developments in our business</li>
            </ul>
            <p className="section-text">
              We usually only disclose Personal Information to our service providers who assist in operating 
              the Website. Your Personal Information may occasionally be accessible to maintenance and support 
              personnel acting in the normal course of their duties.
            </p>
            <p className="section-text">
              By using our Website, you consent to receiving direct marketing material. We will only use your 
              Personal Information for this purpose if:
            </p>
            <ul className="privacy-list">
              <li>we collected the information directly from you, and</li>
              <li>it is material you would reasonably expect to receive from us.</li>
            </ul>
            <p className="section-text">
              Our marketing material includes a simple means to unsubscribe at any time. We do not use 
              Sensitive Information for marketing purposes.
            </p>
          </section>

          {/* Security, access and correction */}
          <section className="privacy-section">
            <div className="section-icon">
              <Shield size={32} />
            </div>
            <h2 className="section-title">Security, access and correction</h2>
            <p className="section-text">
              We store Personal Information securely to protect it from unauthorised access, misuse, 
              modification, or disclosure. When no longer required, we take reasonable steps to destroy 
              or de-identify it.
            </p>
            <p className="section-text">
              Most Personal Information in our client files and records is kept for up to seven years 
              to meet our record-keeping obligations.
            </p>
            <p className="section-text">
              Under the Australian Privacy Principles (APPs), you are entitled to:
            </p>
            <ul className="privacy-list">
              <li>obtain access to the Personal Information we hold about you (APP 12)</li>
              <li>request correction of inaccurate information (APP 13)</li>
            </ul>
            <p className="section-text">
              To request access or correction, please contact us in writing using the details below.
            </p>
          </section>

          {/* Complaint procedure */}
          <section className="privacy-section">
            <div className="section-icon">
              <AlertCircle size={32} />
            </div>
            <h2 className="section-title">Complaint procedure</h2>
            <p className="section-text">
              If you have a complaint about how we manage your Personal Information, please contact us 
              using the details below.
            </p>
            <p className="section-text">
              All complaints will be considered by Nfinity Holdings Pty Ltd, and we may seek further 
              information to clarify your concerns. If we agree your complaint is well-founded, we will 
              work with you to resolve the issue.
            </p>
            <p className="section-text">
              If you remain dissatisfied, you may refer the matter to the Office of the Australian 
              Information Commissioner (OAIC).
            </p>
          </section>

          {/* Overseas transfer */}
          <section className="privacy-section">
            <div className="section-icon">
              <Globe size={32} />
            </div>
            <h2 className="section-title">Overseas transfer</h2>
            <p className="section-text">
              Your Personal Information will not be disclosed to recipients outside Australia unless 
              you expressly request it.
            </p>
            <p className="section-text">
              If you request such a transfer, the overseas recipient will not be required to comply with 
              the Australian Privacy Principles, and we will not be liable for any mishandling of your 
              information.
            </p>
          </section>

          {/* GDPR */}
          <section className="privacy-section">
            <div className="section-icon">
              <Globe size={32} />
            </div>
            <h2 className="section-title">GDPR</h2>
            <p className="section-text">
              In some circumstances, the European Union General Data Protection Regulation (GDPR) provides 
              additional protection to individuals located in Europe.
            </p>
            <p className="section-text">
              However, our Website does not specifically target customers in the EU, nor do we monitor 
              behaviour of EU residents. Accordingly, the GDPR does not apply.
            </p>
          </section>

          {/* Contact */}
          <section className="privacy-section privacy-contact-section">
            <div className="section-icon">
              <Mail size={32} />
            </div>
            <h2 className="section-title">How to contact us about privacy</h2>
            <p className="section-text">
              If you have queries, wish to access your Personal Information, or have a complaint about 
              our privacy practices, please contact us at:
            </p>
            <a href="mailto:getintouch@crikeytech.com" className="contact-email">
              <Mail size={20} />
              getintouch@crikeytech.com
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;